import React, { useEffect, useState } from 'react';
import { CopyIcon, CheckIcon, SmartphoneIcon, UserIcon } from 'lucide-react';
type InvitationPreviewProps = {
  coupleNames: string;
  weddingTitle: string;
  destination: string;
  link: string;
  customNote: string;
};
const LANGUAGES = [{
  id: 'en',
  label: 'English'
}, {
  id: 'hi',
  label: 'Hindi + English'
}, {
  id: 'pa',
  label: 'Punjabi + English'
}, {
  id: 'gu',
  label: 'Gujarati + English'
}];
export function InvitationPreview({
  coupleNames,
  weddingTitle,
  destination,
  link,
  customNote
}: InvitationPreviewProps) {
  const [language, setLanguage] = useState('en');
  const [copied, setCopied] = useState(false);
  const [editableText, setEditableText] = useState('');
  // Generate base text based on language and props
  useEffect(() => {
    const names = coupleNames || '[Couple Names]';
    const dest = destination || '[Destination]';
    const title = weddingTitle || `${names}'s Wedding`;
    const inviteLink = link || 'https://destapass.com/join/...';
    const note = customNote ? `\n\n"${customNote}"` : '';
    let generated = '';
    if (language === 'en') {
      generated = `We are so excited to celebrate our wedding with you! 💍✨\n\nWhile we wish everyone could join us in ${dest}, we know a destination wedding makes that difficult. But we couldn't imagine celebrating without you being a part of it.\n\nWe've set up a private wedding community on Desta Pass so you can follow our journey, see live photos, and feel like you're right there with us!${note}\n\nPlease join our wedding space here:\n${inviteLink}\n\nWith love,\n${names}`;
    } else if (language === 'hi') {
      generated = `Namaste! 🙏 We are so excited to celebrate our wedding with you! 💍✨\n\nWhile we wish everyone could join us in ${dest}, we know traveling isn't always possible. Par aapke bina celebration adhura hai (But the celebration is incomplete without you).\n\nWe've set up a private wedding community on Desta Pass so you can follow our journey, see live photos, and feel like you're right there with us!${note}\n\nPlease join our wedding space here:\n${inviteLink}\n\nSade valon bahut pyar (With lots of love),\n${names}`;
    } else if (language === 'pa') {
      generated = `Sat Sri Akal! 🙏 We are so excited to celebrate our wedding with you! 💍✨\n\nWhile we wish everyone could join us in ${dest}, we know traveling isn't always possible. Par tuhade bina raunak nahi lagni (But it won't be the same without you).\n\nWe've set up a private wedding community on Desta Pass so you can follow our journey, see live photos, and feel like you're right there with us!${note}\n\nPlease join our wedding space here:\n${inviteLink}\n\nPyar sahit (With love),\n${names}`;
    } else if (language === 'gu') {
      generated = `Jai Shri Krishna! 🙏 We are so excited to celebrate our wedding with you! 💍✨\n\nWhile we wish everyone could join us in ${dest}, we know traveling isn't always possible. Tamari haajri vagar anand adhuro chhe (The joy is incomplete without your presence).\n\nWe've set up a private wedding community on Desta Pass so you can follow our journey, see live photos, and feel like you're right there with us!${note}\n\nPlease join our wedding space here:\n${inviteLink}\n\nPrem thi (With love),\n${names}`;
    }
    setEditableText(generated);
  }, [coupleNames, weddingTitle, destination, link, customNote, language]);
  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="grid lg:grid-cols-2 gap-8" data-id="element-412">
      {/* Editor Side */}
      <div className="space-y-6" data-id="element-413">
        <div data-id="element-414">
          <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-415">
            Select Language Tone
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-416">
            {LANGUAGES.map(lang => <button key={lang.id} onClick={() => setLanguage(lang.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${language === lang.id ? 'bg-rose-100 text-rose-700 border-2 border-rose-200' : 'bg-white text-gray-600 border border-gray-200 hover:border-rose-200 hover:bg-rose-50'}`} data-id="element-417">
                {lang.label}
              </button>)}
          </div>
        </div>

        <div data-id="element-418">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-419">
            Customize Message
          </label>
          <textarea value={editableText} onChange={e => setEditableText(e.target.value)} rows={10} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all resize-none text-gray-700 leading-relaxed" data-id="element-420" />
        </div>

        <button onClick={handleCopy} className="w-full py-3 px-4 bg-white border-2 border-rose-100 text-rose-600 rounded-xl font-semibold hover:bg-rose-50 transition-colors flex items-center justify-center gap-2" data-id="element-421">
          {copied ? <CheckIcon className="h-5 w-5" data-id="element-422" /> : <CopyIcon className="h-5 w-5" data-id="element-423" />}
          {copied ? 'Copied to Clipboard!' : 'Copy Invitation Text'}
        </button>
      </div>

      {/* Preview Side */}
      <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100 flex flex-col items-center justify-center" data-id="element-424">
        <div className="flex items-center gap-2 text-gray-500 mb-6" data-id="element-425">
          <SmartphoneIcon className="h-5 w-5" data-id="element-426" />
          <span className="text-sm font-medium uppercase tracking-wider" data-id="element-427">
            WhatsApp Preview
          </span>
        </div>

        {/* WhatsApp Bubble Mockup */}
        <div className="w-full max-w-sm bg-[#EFEAE2] rounded-3xl p-4 shadow-inner relative overflow-hidden" data-id="element-428">
          {/* WhatsApp Header fake */}
          <div className="bg-[#00A884] text-white p-3 -mx-4 -mt-4 mb-4 flex items-center gap-3" data-id="element-429">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center" data-id="element-430">
              <UserIcon className="h-5 w-5 text-white" data-id="element-431" />
            </div>
            <div className="font-medium text-sm" data-id="element-432">Family Group</div>
          </div>

          {/* Message Bubble */}
          <div className="bg-white rounded-2xl rounded-tl-none p-3 shadow-sm relative" data-id="element-433">
            <p className="text-[15px] text-gray-800 whitespace-pre-wrap font-sans leading-snug" data-id="element-434">
              {editableText}
            </p>
            <div className="text-[10px] text-gray-400 text-right mt-1" data-id="element-435">
              10:42 AM
            </div>
          </div>
        </div>
      </div>
    </div>;
}